import { useEffect } from 'react'
import { BrainCircuit, AlertTriangle } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import useMemoryStore from '@/stores/useMemoryStore'
import { AddMemoryDialog } from './AddMemoryDialog'
import { EditMemoryDialog } from './EditMemoryDialog'
import { DeleteMemoryAlert } from './DeleteMemoryAlert'

interface MemoryListProps {
  organizationId: string
}

export const MemoryList = ({ organizationId }: MemoryListProps) => {
  const { memories, fetchMemories, isLoading, error } = useMemoryStore()

  useEffect(() => {
    if (organizationId) {
      fetchMemories(organizationId)
    }
  }, [organizationId, fetchMemories])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )
    }

    if (error) {
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load memories</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )
    }

    if (memories.length === 0) {
      return (
        <p className="text-muted-foreground text-center py-4">
          No memories found for this organization.
        </p>
      )
    }

    return (
      <div className="space-y-4">
        {memories.map((memory) => (
          <Card key={memory.id}>
            <CardContent className="p-4 flex items-start justify-between gap-4">
              <div className="flex-grow">
                <p className="text-sm whitespace-pre-wrap">{memory.content}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Created: {new Date(memory.createdAt).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center">
                <EditMemoryDialog orgId={organizationId} memory={memory} />
                <DeleteMemoryAlert
                  orgId={organizationId}
                  memoryId={memory.id}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BrainCircuit className="h-5 w-5" /> Memories
          </CardTitle>
          <CardDescription>
            Organizational memories for context.
          </CardDescription>
        </div>
        <AddMemoryDialog orgId={organizationId} />
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  )
}
